import cn from "classnames";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";

import { StrapiComponentType, DeepSkyType } from "@/src/type";

import styles from "./Pricing.module.scss";

import PricingCard from "./PricingCard";

const checkFeatureExist = (attributes: Array<Record<string, any>>) => {
  return (attributes ?? []).filter((opt) => opt["unit.amenities"] ?? "").length > 0;
};

const minPriceSort = (unit: DeepSkyType.AssetType.PropertyInventory.Unit, minPricePeriod: string) => {
  const price = unit?.prices?.filter((price) => price.pricePeriod === minPricePeriod);
  if ((price ?? []).length === 0) {
    return 999999999;
  }

  const minPrice = price.reduce((min, price) => (price.basePrice < min.basePrice ? price : min), price[0]);
  if (!minPrice?.basePrice || minPrice.basePrice === 0) {
    return 999999999;
  }

  return minPrice.basePrice;
};

interface PricingProps {
  propertyUnitDetails: StrapiComponentType.Section.PropertyUnitDetails;
  floorplan: DeepSkyType.AssetType.PropertyInventory.MarketingFloorPlan;
}

function Pricing({ propertyUnitDetails, floorplan }: PricingProps) {
  // determine if the are any units setup
  const units = (floorplan?.unitTypes ?? []).flatMap((unitType) => unitType.units);

  // does at least one unit have features
  const hasFeatures = (units ?? []).filter((unit) => checkFeatureExist(unit.attributes)).length > 0;

  // sort the units by the min price
  const minPricePeriod = floorplan?.minPrice?.pricePeriod;
  const sortedUnits = useMemo(() => {
    return (units ?? []).sort((a, b) => minPriceSort(a, minPricePeriod) - minPriceSort(b, minPricePeriod));
  }, [units, minPricePeriod]);

  if (!units || units.length === 0) {
    return <></>;
  }

  return (
    <div
      className={styles.container}
      id="unit-pricing"
    >
      <Typography
        variant="h6"
        className={styles.title}
      >
        {(sortedUnits ?? []).length} {propertyUnitDetails.perfectMatchTitle}
      </Typography>
      <div className={cn(styles.row, styles.desktop)}>
        <div className={cn(styles.group, hasFeatures ? styles.hasFeatures : "")}>
          <Typography
            variant="body1"
            className={styles.headerLabel}
          >
            {propertyUnitDetails.tableUnitNameTitle ?? "Unit Name"}
          </Typography>
          <Typography
            variant="body1"
            className={styles.headerLabel}
          >
            {propertyUnitDetails.tableBestPriceTitle ?? "Best Price"}
          </Typography>
          <Typography
            variant="body1"
            className={styles.headerLabel}
          >
            {propertyUnitDetails.tableAvailableTitle ?? "Available"}
          </Typography>
          {hasFeatures && (
            <Typography
              variant="body1"
              className={styles.headerLabel}
            >
              {propertyUnitDetails.tableFeaturesTitle ?? "Features"}
            </Typography>
          )}
        </div>
        <div className={styles.end} />
      </div>
      {(sortedUnits ?? []).map((unit) => (
        <PricingCard
          key={unit.fid}
          propertyUnitDetails={propertyUnitDetails}
          unit={unit}
          minPricePeriod={minPricePeriod}
          hasFeatures={hasFeatures}
        />
      ))}
      {propertyUnitDetails.pricingDisclaimer && (
        <div className={styles.disclaimer}>
          <Typography
            variant="body1"
            className={styles.disclaimerText}
          >
            {propertyUnitDetails.pricingDisclaimer}
          </Typography>
        </div>
      )}
    </div>
  );
}

export default Pricing;
